import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, CircularProgress, Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import { getPreparations, updateStock } from '../../services/PreparationService';
import AddEditPreparationModal from './AddEditPreparationModal';
import EditStockModal from './EditStockModal';
import StockHistoryModal from '../StockHistory/StockHistoryModal';
import { REFERENCE_TYPE } from '../../services/StockEventService';
import ViewPreparationModal from './ViewPreparationModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { getUnitById, permissionOptions } from '../../utils/Enums';
import IOSSwitch from '../Switch/IOSSwitch';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';
import { formatMoney } from '../../utils/currency';

function PreparationsTable({ locationId, isBranch }) {
  const { t } = useTranslation();
  
  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);
  const canViewPreparations = hasPermission(permissionOptions.find(p => p.label === 'VIEW_PREPARATIONS').id);
  const canUpdatePreparationConfig = hasPermission(permissionOptions.find(p => p.label === 'UPDATE_PREPARATIONS').id);
  const canDeletePreparations = hasPermission(permissionOptions.find(p => p.label === 'DELETE_PREPARATIONS').id);
  const canManageLocationVisibility = hasPermission(permissionOptions.find(p => p.label === 'VIEW_BUSINESS_UNITS').id);
  const canViewCost = hasPermission(permissionOptions.find(p => p.label === 'VIEW_COST_PRODUCTS').id);
  const canViewStockHistory = hasPermission(permissionOptions.find(p => p.label === 'VIEW_STOCK_HISTORY').id);

  const [preparations, setPreparations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPreparation, setSelectedPreparation] = useState(null);
  const [modalState, setModalState] = useState({
    add: false,
    edit: false,
    editStock: false,
    view: false,
    delete: false,
    stockHistory: false
  });
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);

  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  const fetchPreparations = useCallback(async (page, pageSize, search, currentSortModel = sortModel, currentFilterModel = filterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const data = await getPreparations(locationId, page, pageSize, cleanSearch, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      });

      if (data && typeof data === 'object') {
        setPreparations(data.preparations || data.data || []);
        setTotalRows(data.total || data.totalCount || 0);
      } else {
        setPreparations(data || []);
        setTotalRows(data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching preparations:', error);
      setPreparations([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [locationId, sortModel, filterModel]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchPreparations(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchPreparations, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);
    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchPreparations(1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    }, 500);
  };

  const handleModal = (type, open, preparation = null) => {
    setModalState({ ...modalState, [type]: open });
    setSelectedPreparation(preparation);
  };

  const handleAddPreparation = async (newPreparation) => {
    // Refetch data after adding
    fetchPreparations(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('add', false);
  };

  const handleEditPreparation = async (updatedPreparation) => {
    // Refetch data after editing
    fetchPreparations(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('edit', false);
  };

  const handleEditStockPreparation = async (updatedPreparation) => {
    // Refetch data after editing stock
    fetchPreparations(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('editStock', false);
  };

  const handleDeletePreparation = async () => {
    // Refetch data after deleting
    fetchPreparations(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    handleModal('delete', false);
  };

  const handleToggleVisibility = useCallback(async (preparation, checked) => {
    try {
      const response = await updateStock(preparation.id, {
        location_id: locationId,
        stock: preparation.stock ?? 0,
        minimum_stock: preparation.minimumStock ?? 0,
        suggested_stock: preparation.suggestedStock ?? 0,
        visible_in_location: checked,
      });
      setPreparations((prev) => prev.map((item) => (item.id === preparation.id ? response : item)));
    } catch (error) {
      console.error('Error updating preparation visibility:', error);
    }
  }, [locationId]);

  // El filtrado ahora se hace en el servidor, no necesitamos filteredPreparations

  const columns = [
    { field: 'name', headerName: t('name'), flex: 2 },
    ...(canViewCost ? [{
      field: 'cost',
      headerName: t('cost'),
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value, row) =>
        value !== null && value !== undefined
          ? formatMoney(value, row?.costCurrency)
          : 'N/A'
    }] : []),
    {
      field: 'stock',
      headerName: t('stock'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        return row.stock.toLocaleString('de-DE');
      }
    },
    {
      field: 'minimumStock',
      headerName: t('minimum_stock'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        return row.minimumStock.toLocaleString('de-DE');
      }
    },
    {
      field: 'suggestedStock',
      headerName: t('suggested_stock'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        return row.suggestedStock.toLocaleString('de-DE');
      }
    },
    ...(canManageLocationVisibility ? [{
      field: 'visibleInLocation',
      headerName: t('visible_in_location'),
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IOSSwitch
            checked={!!params.row.visibleInLocation}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => handleToggleVisibility(params.row, event.target.checked)}
          />
        </Box>
      )
    }] : []),
    { 
      field: 'batch', 
      headerName: t('batch'), 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        return row.batch.toLocaleString('de-DE');
      }
    },
    { 
      field: 'unit', 
      headerName: t('unit'), 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center', 
      valueGetter: (params) => getUnitById(params)?.label
    }
  ];

  if (!isBranch) {
      columns.push({ 
        field: 'actions',
        headerName: t('actions'),
        flex: 1.7,
        sortable: false, 
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <>
            {canViewPreparations && (
              !params.row.recipeDetailsHidden && (
                <IconButton onClick={() => handleModal('view', true, params.row)}>
                  {getIcon('eye')}
                </IconButton>
              )
            )}
            {canUpdatePreparationConfig && !isBranch && (
              <IconButton onClick={() => handleModal('edit', true, params.row)}>
                {getIcon('settings')}
              </IconButton>
            )}
            {canUpdatePreparationConfig && (
              <IconButton onClick={() => handleModal('editStock', true, params.row)}>
                {getIcon('bagHandle')}
              </IconButton>
            )}
            {canViewStockHistory && (
              <IconButton onClick={() => handleModal('stockHistory', true, params.row)}>
                {getIcon('time')}
              </IconButton>
            )}
            {canDeletePreparations && (
              <IconButton onClick={() => handleModal('delete', true, params.row)}>
                {getIcon('trash')}
              </IconButton>
            )}
          </>
        )
      });
    }

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <TextField
            label={t('search')}
            placeholder={t('search_by_preparation_name')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ width: '300px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRefreshButton
              loading={loading}
              onClick={() => fetchPreparations(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
            />
            {hasPermission(permissionOptions.find(p => p.label === 'CREATE_PREPARATIONS').id) && (
              <Button variant="outlined" startIcon={getIcon('add')} onClick={() => handleModal('add', true)}>
                {t('add_preparation')}
              </Button>
            )}
          </Box>
        </Box>
        
        <DataGrid
          loading={loading}
          rows={preparations}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          filterMode="server"
          pageSizeOptions={[10, 25, 50]}
          paginationMode="server"
          rowCount={totalRows}
          autoHeight
          getRowClassName={(params) => {
            const { stock, minimumStock, suggestedStock } = params.row;
            
            if (stock < minimumStock) {
              return 'row-stock-red';
            }
            
            if (stock < suggestedStock) {
              return 'row-stock-yellow';
            }
            
            return '';
          }}
          sx={{
            '& .row-stock-red': { backgroundColor: '#ffebee !important' },
            '& .row-stock-yellow': { backgroundColor: '#fff9e6 !important' },
            '& .MuiDataGrid-row:hover': { opacity: 0.9 },
          }}
        />
      </Paper>
      
      {/* Modales */}
      {modalState.add && <AddEditPreparationModal locationId={locationId} open={modalState.add} onClose={() => handleModal('add', false)} onSave={handleAddPreparation} />}
      {modalState.edit && <AddEditPreparationModal locationId={locationId} open={modalState.edit} preparation={selectedPreparation} onClose={() => handleModal('edit', false)} onSave={handleEditPreparation} />}
      {modalState.editStock && <EditStockModal locationId={locationId} open={modalState.editStock} preparation={selectedPreparation} onClose={() => handleModal('editStock', false)} onSave={handleEditStockPreparation} showVisibilityToggle={canManageLocationVisibility} />}
      {modalState.view && <ViewPreparationModal locationId={locationId} open={modalState.view} preparation={selectedPreparation} onClose={() => handleModal('view', false)} />}
      {modalState.delete && <DeleteConfirmModal open={modalState.delete} onClose={() => handleModal('delete', false)} onDelete={handleDeletePreparation} preparation={selectedPreparation} />}
      {modalState.stockHistory && (
        <StockHistoryModal
          open={modalState.stockHistory}
          onClose={() => handleModal('stockHistory', false)}
          locationId={locationId}
          referenceType={REFERENCE_TYPE.PREPARATION}
          referenceId={selectedPreparation?.id}
          itemName={selectedPreparation?.name}
        />
      )}
    </>
  );
}

export default PreparationsTable;
